import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Calendar } from '../components/ui/calendar';
import {
  Calculator,
  ArrowLeft,
  Calendar as CalendarIcon,
  Clock,
  AlertTriangle,
  CheckCircle,
  Info,
  ChevronDown,
  ChevronUp,
  FileText,
  Scale,
  Briefcase,
  Building2,
  Gavel,
  Loader2,
  Search,
  Filter,
  BookOpen
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

// Category icons and colors
const CATEGORY_CONFIG = {
  tributario: {
    icon: Calculator,
    color: 'blue',
    bgClass: 'bg-blue-500/10',
    borderClass: 'border-blue-500/30',
    textClass: 'text-blue-400',
    name: 'Derecho Tributario'
  },
  constitucional: {
    icon: Scale,
    color: 'purple',
    bgClass: 'bg-purple-500/10',
    borderClass: 'border-purple-500/30',
    textClass: 'text-purple-400',
    name: 'Derecho Constitucional'
  },
  administrativo: {
    icon: Building2,
    color: 'amber',
    bgClass: 'bg-amber-500/10',
    borderClass: 'border-amber-500/30',
    textClass: 'text-amber-400',
    name: 'Derecho Administrativo'
  },
  laboral: {
    icon: Briefcase,
    color: 'green',
    bgClass: 'bg-green-500/10',
    borderClass: 'border-green-500/30',
    textClass: 'text-green-400',
    name: 'Derecho Laboral'
  },
  civil: {
    icon: Gavel,
    color: 'rose',
    bgClass: 'bg-rose-500/10',
    borderClass: 'border-rose-500/30',
    textClass: 'text-rose-400',
    name: 'Derecho Civil'
  },
  otros: {
    icon: FileText,
    color: 'slate',
    bgClass: 'bg-slate-500/10',
    borderClass: 'border-slate-500/30',
    textClass: 'text-slate-400',
    name: 'Otros'
  }
};

// Status badge component
const StatusBadge = ({ status, daysRemaining }) => {
  const config = {
    vencido: { bg: 'bg-red-500/20', border: 'border-red-500/50', text: 'text-red-400', label: 'Vencido' },
    urgente: { bg: 'bg-red-500/20', border: 'border-red-500/50', text: 'text-red-400', label: 'Urgente' },
    proximo: { bg: 'bg-amber-500/20', border: 'border-amber-500/50', text: 'text-amber-400', label: 'Próximo' },
    vigente: { bg: 'bg-green-500/20', border: 'border-green-500/50', text: 'text-green-400', label: 'Vigente' }
  };
  
  const { bg, border, text, label } = config[status] || config.vigente;
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${bg} ${border} ${text} border`}>
      {status === 'vencido' || status === 'urgente' ? (
        <AlertTriangle className="w-3 h-3" />
      ) : status === 'proximo' ? (
        <Clock className="w-3 h-3" />
      ) : (
        <CheckCircle className="w-3 h-3" />
      )}
      {label} ({daysRemaining} días)
    </span>
  );
};

// Procedure card component
const ProcedureCard = ({ procedure, onSelect, selected }) => {
  const category = CATEGORY_CONFIG[procedure.category] || CATEGORY_CONFIG.otros;
  const Icon = category.icon;
  
  return (
    <button
      onClick={() => onSelect(procedure)}
      data-testid={`procedure-${procedure.type}`}
      className={`w-full text-left p-4 rounded-xl border transition-all ${
        selected?.type === procedure.type
          ? `${category.bgClass} ${category.borderClass} ring-2 ring-offset-2 ring-offset-slate-900 ring-${category.color}-500/50`
          : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${category.bgClass}`}>
          <Icon className={`w-5 h-5 ${category.textClass}`} />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-white truncate">{procedure.name}</h4>
          <p className="text-xs text-slate-400 mt-0.5">
            {procedure.days} días {procedure.day_type === 'habiles' ? 'hábiles' : 'corridos'}
          </p>
          <p className="text-xs text-slate-500 mt-1 truncate">{procedure.legal_basis}</p>
        </div>
      </div>
    </button>
  );
};

// Result card component
const ResultCard = ({ result }) => {
  if (!result) return null;
  
  const category = CATEGORY_CONFIG[result.procedure?.category] || CATEGORY_CONFIG.otros;
  const Icon = category.icon;
  
  return (
    <div className={`p-6 rounded-xl border ${category.bgClass} ${category.borderClass}`}>
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${category.bgClass}`}>
            <Icon className={`w-6 h-6 ${category.textClass}`} />
          </div>
          <div>
            <h3 className="font-semibold text-white">{result.procedure.name}</h3>
            <p className="text-sm text-slate-400">{result.procedure.legal_basis}</p>
          </div>
        </div>
        <StatusBadge 
          status={result.status.status} 
          daysRemaining={result.status.days_remaining} 
        />
      </div>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="bg-slate-900/50 rounded-lg p-3">
          <p className="text-xs text-slate-400 mb-1">Fecha Inicio</p>
          <p className="font-medium text-white">
            {format(new Date(result.calculation.start_date), "dd 'de' MMMM, yyyy", { locale: es })}
          </p>
        </div>
        <div className="bg-slate-900/50 rounded-lg p-3">
          <p className="text-xs text-slate-400 mb-1">Fecha Vencimiento</p>
          <p className={`font-medium ${result.status.is_expired ? 'text-red-400' : 'text-green-400'}`}>
            {result.calculation.deadline_formatted}
          </p>
        </div>
      </div>
      
      <div className="bg-slate-900/50 rounded-lg p-3 mb-4">
        <p className="text-xs text-slate-400 mb-2">Descripción</p>
        <p className="text-sm text-slate-300">{result.procedure.description}</p>
        <p className="text-xs text-slate-500 mt-2">
          <span className="text-slate-400">Evento inicial:</span> {result.procedure.start_event}
        </p>
      </div>
      
      {result.holidays_in_period?.length > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
          <p className="text-xs text-amber-400 font-medium mb-1 flex items-center gap-1">
            <Info className="w-3 h-3" />
            Feriados en el período
          </p>
          <p className="text-xs text-slate-400">
            {result.holidays_in_period.join(', ')}
          </p>
        </div>
      )}
      
      {result.procedure.fatal && (
        <div className="mt-3 bg-red-500/10 border border-red-500/30 rounded-lg p-3">
          <p className="text-xs text-red-400 font-medium flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            Plazo Fatal - No admite prórroga
          </p>
        </div>
      )}
    </div>
  );
};

// Custom deadline result
const CustomResultCard = ({ result }) => {
  if (!result) return null;
  
  return (
    <div className="p-6 rounded-xl border bg-cyan-500/10 border-cyan-500/30">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-cyan-500/20">
            <Calculator className="w-6 h-6 text-cyan-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Cálculo Personalizado</h3>
            <p className="text-sm text-slate-400">
              {result.calculation.days} días {result.calculation.day_type === 'habiles' ? 'hábiles' : 'corridos'}
            </p>
          </div>
        </div>
        <StatusBadge 
          status={result.status.status} 
          daysRemaining={result.status.days_remaining} 
        />
      </div>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="bg-slate-900/50 rounded-lg p-3">
          <p className="text-xs text-slate-400 mb-1">Fecha Inicio</p>
          <p className="font-medium text-white">
            {format(new Date(result.calculation.start_date), "dd 'de' MMMM, yyyy", { locale: es })}
          </p>
        </div>
        <div className="bg-slate-900/50 rounded-lg p-3">
          <p className="text-xs text-slate-400 mb-1">Fecha Vencimiento</p>
          <p className={`font-medium ${result.status.is_expired ? 'text-red-400' : 'text-green-400'}`}>
            {result.calculation.deadline_formatted}
          </p>
        </div>
      </div>
      
      {result.holidays_in_period?.length > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-3">
          <p className="text-xs text-amber-400 font-medium mb-1">Feriados excluidos del cómputo:</p>
          <p className="text-xs text-slate-400">{result.holidays_in_period.join(', ')}</p>
        </div>
      )}
      
      {result.weekends_in_period > 0 && (
        <p className="text-xs text-slate-500">
          Fines de semana en el período: {result.weekends_in_period}
        </p>
      )}
    </div>
  );
};

const LegalDeadlinesPage = () => {
  const { api } = useAuth();
  
  // State
  const [activeTab, setActiveTab] = useState('procedures'); // procedures, custom, holidays
  const [procedures, setProcedures] = useState([]);
  const [categorizedProcedures, setCategorizedProcedures] = useState({});
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [selectedProcedure, setSelectedProcedure] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [result, setResult] = useState(null);
  const [customResult, setCustomResult] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedCategories, setExpandedCategories] = useState({});
  
  // Custom calculation state
  const [customDays, setCustomDays] = useState(10);
  const [customDayType, setCustomDayType] = useState('habiles');
  
  // Holidays state
  const [holidays, setHolidays] = useState([]);
  const [holidayYear, setHolidayYear] = useState(new Date().getFullYear());
  
  // Fetch procedures on load
  useEffect(() => {
    fetchProcedures();
  }, []);
  
  const fetchProcedures = async () => {
    try {
      setLoading(true);
      const response = await api.get('/deadlines/procedures');
      setProcedures(response.data.procedures || []);
      setCategorizedProcedures(response.data.by_category || {});
      
      // Expand first category by default
      if (response.data.by_category) {
        const firstCategory = Object.keys(response.data.by_category)[0];
        if (firstCategory) {
          setExpandedCategories({ [firstCategory]: true });
        }
      }
    } catch (error) {
      console.error('Error fetching procedures:', error);
      toast.error('Error al cargar procedimientos legales');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchHolidays = async (year) => {
    try {
      const response = await api.get(`/deadlines/holidays/${year}`);
      setHolidays(response.data.holidays || []);
    } catch (error) {
      console.error('Error fetching holidays:', error);
      toast.error('Error al cargar feriados');
    }
  };
  
  useEffect(() => {
    if (activeTab === 'holidays') {
      fetchHolidays(holidayYear);
    }
  }, [activeTab, holidayYear]);
  
  const calculateDeadline = async () => {
    if (!selectedProcedure) {
      toast.error('Selecciona un procedimiento');
      return;
    }
    
    setCalculating(true);
    try {
      const response = await api.post('/deadlines/calculate', {
        procedure_type: selectedProcedure.type,
        start_date: format(selectedDate, 'yyyy-MM-dd')
      });
      setResult(response.data);
      toast.success('Plazo calculado exitosamente');
    } catch (error) {
      console.error('Error calculating deadline:', error);
      toast.error(error.response?.data?.detail || 'Error al calcular plazo');
    } finally {
      setCalculating(false);
    }
  };
  
  const calculateCustomDeadline = async () => {
    setCalculating(true);
    try {
      const response = await api.post('/deadlines/calculate-custom', {
        start_date: format(selectedDate, 'yyyy-MM-dd'),
        days: customDays,
        day_type: customDayType
      });
      setCustomResult(response.data);
      toast.success('Plazo personalizado calculado');
    } catch (error) {
      console.error('Error calculating custom deadline:', error);
      toast.error('Error al calcular plazo personalizado');
    } finally {
      setCalculating(false);
    }
  };
  
  // Filter procedures
  const filteredProcedures = procedures.filter(proc => {
    const matchesSearch = proc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         proc.legal_basis.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || proc.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });
  
  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };
  
  return (
    <div className="min-h-screen bg-background" data-testid="legal-deadlines-page">
      {/* Header */}
      <header className="bg-card border-b border-border px-4 py-4 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-slate-400 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                <Clock className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-lg">Calculadora de Plazos Legales</h1>
                <p className="text-xs text-slate-400">Plazos judiciales y administrativos chilenos</p>
              </div>
            </div>
          </div>
        </div>
      </header>
      
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6 border-b border-slate-700 pb-2 overflow-x-auto">
          <button
            onClick={() => setActiveTab('procedures')}
            data-testid="tab-procedures"
            className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-2 ${
              activeTab === 'procedures'
                ? 'bg-indigo-500/20 text-indigo-400 border-b-2 border-indigo-500'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <BookOpen className="w-4 h-4" />
            Procedimientos Legales
          </button>
          <button
            onClick={() => setActiveTab('custom')}
            data-testid="tab-custom"
            className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-2 ${
              activeTab === 'custom'
                ? 'bg-cyan-500/20 text-cyan-400 border-b-2 border-cyan-500'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <Calculator className="w-4 h-4" />
            Cálculo Personalizado
          </button>
          <button
            onClick={() => setActiveTab('holidays')}
            data-testid="tab-holidays"
            className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-2 ${
              activeTab === 'holidays'
                ? 'bg-amber-500/20 text-amber-400 border-b-2 border-amber-500'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <CalendarIcon className="w-4 h-4" />
            Feriados Chile
          </button>
        </div>
        
        {/* Procedures Tab */}
        {activeTab === 'procedures' && (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Left Column - Procedure Selection */}
            <div className="space-y-4">
              {/* Search and Filter */}
              <div className="flex gap-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Buscar procedimiento..."
                    data-testid="search-procedures"
                    className="w-full pl-10 pr-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:border-indigo-500 focus:outline-none"
                  />
                </div>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  data-testid="filter-category"
                  className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:border-indigo-500 focus:outline-none"
                >
                  <option value="all">Todas las categorías</option>
                  {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                    <option key={key} value={key}>{config.name}</option>
                  ))}
                </select>
              </div>
              
              {/* Procedures List */}
              <div className="bg-card border border-border rounded-xl overflow-hidden">
                <div className="p-4 border-b border-border">
                  <h3 className="font-semibold text-white flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-indigo-400" />
                    Selecciona el Procedimiento
                  </h3>
                  <p className="text-xs text-slate-400 mt-1">{filteredProcedures.length} procedimientos disponibles</p>
                </div>
                
                <div className="max-h-[500px] overflow-y-auto p-4 space-y-3">
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
                    </div>
                  ) : searchTerm || selectedCategory !== 'all' ? (
                    // Show flat list when filtering
                    filteredProcedures.map((proc) => (
                      <ProcedureCard
                        key={proc.type}
                        procedure={proc}
                        onSelect={setSelectedProcedure}
                        selected={selectedProcedure}
                      />
                    ))
                  ) : (
                    // Show categorized list
                    Object.entries(categorizedProcedures).map(([categoryKey, categoryData]) => {
                      const category = CATEGORY_CONFIG[categoryKey] || CATEGORY_CONFIG.otros;
                      const Icon = category.icon;
                      const isExpanded = expandedCategories[categoryKey];
                      
                      return (
                        <div key={categoryKey} className="border border-slate-700 rounded-xl overflow-hidden">
                          <button
                            onClick={() => toggleCategory(categoryKey)}
                            className={`w-full flex items-center justify-between p-3 ${category.bgClass} hover:opacity-90 transition-opacity`}
                          >
                            <div className="flex items-center gap-2">
                              <Icon className={`w-4 h-4 ${category.textClass}`} />
                              <span className={`font-medium ${category.textClass}`}>{categoryData.name}</span>
                              <span className="text-xs text-slate-400">({categoryData.procedures?.length || 0})</span>
                            </div>
                            {isExpanded ? (
                              <ChevronUp className="w-4 h-4 text-slate-400" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-slate-400" />
                            )}
                          </button>
                          {isExpanded && (
                            <div className="p-3 space-y-2 bg-slate-900/50">
                              {categoryData.procedures?.map((proc) => (
                                <ProcedureCard
                                  key={proc.type}
                                  procedure={{ ...proc, category: categoryKey }}
                                  onSelect={setSelectedProcedure}
                                  selected={selectedProcedure}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
            
            {/* Right Column - Calendar & Results */}
            <div className="space-y-4">
              {/* Date Selection */}
              <div className="bg-card border border-border rounded-xl p-4">
                <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4 text-indigo-400" />
                  Fecha de Inicio del Plazo
                </h3>
                <div className="flex justify-center">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => date && setSelectedDate(date)}
                    locale={es}
                    className="rounded-md border border-slate-700"
                  />
                </div>
                <p className="text-center text-sm text-slate-400 mt-3">
                  Seleccionado: {format(selectedDate, "EEEE, dd 'de' MMMM 'de' yyyy", { locale: es })}
                </p>
              </div>
              
              {/* Calculate Button */}
              <button
                onClick={calculateDeadline}
                disabled={!selectedProcedure || calculating}
                data-testid="calculate-deadline-btn"
                className="w-full py-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl font-medium transition-all flex items-center justify-center gap-2"
              >
                {calculating ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Calculando...
                  </>
                ) : (
                  <>
                    <Calculator className="w-5 h-5" />
                    Calcular Plazo
                  </>
                )}
              </button>
              
              {/* Selected Procedure Info */}
              {selectedProcedure && !result && (
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                  <p className="text-sm text-slate-400 mb-2">Procedimiento seleccionado:</p>
                  <p className="font-medium text-white">{selectedProcedure.name}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {selectedProcedure.days} días {selectedProcedure.day_type === 'habiles' ? 'hábiles' : 'corridos'} — {selectedProcedure.legal_basis}
                  </p>
                </div>
              )}
              
              {/* Result */}
              {result && <ResultCard result={result} />}
            </div>
          </div>
        )}
        
        {/* Custom Calculation Tab */}
        {activeTab === 'custom' && (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Left Column - Input */}
            <div className="space-y-4">
              <div className="bg-card border border-border rounded-xl p-6">
                <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                  <Calculator className="w-5 h-5 text-cyan-400" />
                  Configurar Cálculo Personalizado
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-slate-400 mb-2">Cantidad de días</label>
                    <input
                      type="number"
                      value={customDays}
                      onChange={(e) => setCustomDays(Math.max(1, parseInt(e.target.value) || 1))}
                      min="1"
                      data-testid="custom-days-input"
                      className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-cyan-500 focus:outline-none"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm text-slate-400 mb-2">Tipo de días</label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => setCustomDayType('habiles')}
                        data-testid="day-type-habiles"
                        className={`p-3 rounded-lg border transition-all ${
                          customDayType === 'habiles'
                            ? 'bg-cyan-500/20 border-cyan-500 text-cyan-400'
                            : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600'
                        }`}
                      >
                        <p className="font-medium">Días Hábiles</p>
                        <p className="text-xs mt-1 opacity-70">Excluye fines de semana y feriados</p>
                      </button>
                      <button
                        onClick={() => setCustomDayType('corridos')}
                        data-testid="day-type-corridos"
                        className={`p-3 rounded-lg border transition-all ${
                          customDayType === 'corridos'
                            ? 'bg-cyan-500/20 border-cyan-500 text-cyan-400'
                            : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600'
                        }`}
                      >
                        <p className="font-medium">Días Corridos</p>
                        <p className="text-xs mt-1 opacity-70">Cuenta todos los días calendario</p>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Info Card */}
              <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-xl p-4">
                <h4 className="font-medium text-indigo-400 mb-2 flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  Información Importante
                </h4>
                <ul className="text-sm text-slate-400 space-y-1">
                  <li>• Los días hábiles excluyen sábados, domingos y feriados legales</li>
                  <li>• El cálculo considera los feriados oficiales de Chile</li>
                  <li>• El día de inicio no se cuenta en el cómputo del plazo</li>
                </ul>
              </div>
            </div>
            
            {/* Right Column - Calendar & Results */}
            <div className="space-y-4">
              <div className="bg-card border border-border rounded-xl p-4">
                <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4 text-cyan-400" />
                  Fecha de Inicio
                </h3>
                <div className="flex justify-center">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => date && setSelectedDate(date)}
                    locale={es}
                    className="rounded-md border border-slate-700"
                  />
                </div>
              </div>
              
              <button
                onClick={calculateCustomDeadline}
                disabled={calculating}
                data-testid="calculate-custom-btn"
                className="w-full py-3 bg-gradient-to-r from-cyan-600 to-teal-600 hover:from-cyan-700 hover:to-teal-700 disabled:opacity-50 rounded-xl font-medium transition-all flex items-center justify-center gap-2"
              >
                {calculating ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Calculando...
                  </>
                ) : (
                  <>
                    <Calculator className="w-5 h-5" />
                    Calcular Plazo Personalizado
                  </>
                )}
              </button>
              
              {customResult && <CustomResultCard result={customResult} />}
            </div>
          </div>
        )}
        
        {/* Holidays Tab */}
        {activeTab === 'holidays' && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="p-4 border-b border-border flex items-center justify-between">
                <h3 className="font-semibold text-white flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5 text-amber-400" />
                  Feriados Legales de Chile
                </h3>
                <select
                  value={holidayYear}
                  onChange={(e) => setHolidayYear(parseInt(e.target.value))}
                  data-testid="holiday-year-select"
                  className="px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm"
                >
                  <option value={2024}>2024</option>
                  <option value={2025}>2025</option>
                  <option value={2026}>2026</option>
                </select>
              </div>
              
              <div className="divide-y divide-slate-700">
                {holidays.length === 0 ? (
                  <div className="p-8 text-center">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-amber-500 mb-2" />
                    <p className="text-slate-400">Cargando feriados...</p>
                  </div>
                ) : (
                  holidays.map((holiday, index) => (
                    <div key={index} className="flex items-center justify-between p-4 hover:bg-slate-800/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-500/10 rounded-lg flex items-center justify-center">
                          <CalendarIcon className="w-5 h-5 text-amber-400" />
                        </div>
                        <div>
                          <p className="font-medium text-white">{holiday.formatted}</p>
                          <p className="text-xs text-slate-400 capitalize">{holiday.day_name}</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              
              <div className="p-4 bg-slate-800/50 border-t border-slate-700">
                <p className="text-xs text-slate-400 text-center">
                  Total: {holidays.length} feriados legales en {holidayYear}
                </p>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default LegalDeadlinesPage;
